import { useCallback, useEffect, useMemo, useState } from "react";
import { useNetworkFormContext } from "../../../context/NetworkFormContext";
import { SubnetPool } from "../../../types/subnetPool";
import { getProjectsObject } from "../../../../../../../selectors/projects";
import { useSelector } from "react-redux";
import {
  getCurrentProjectID,
  toastError,
} from "../../../../../../../app_shared_functions";
import useIsMountedRef from "../../../../../../../custom-hooks/useIsMountedRef";
import FetchAPI from "../../../../../../../api/FetchAPI";
import { HandleSelectChange } from "../../../../../../../custom-hooks/form/types";

const useSubnetPools = (
  onChange: HandleSelectChange,
  subnetPool: string | undefined,
) => {
  const { formData, predefined_params } = useNetworkFormContext();
  const region = formData.region || predefined_params?.region;

  const [pools, setPools] = useState<SubnetPool[]>([]);
  const [loading, setLoading] = useState(true);

  const projects = useSelector(getProjectsObject);
  const projectId = getCurrentProjectID(projects, region);
  const isMountedRef = useIsMountedRef();

  const fetchSubnetPools = useCallback(() => {
    FetchAPI.Networking.Subnets.getSubnetPools({
      region: region,
      project_id: projectId,
    })
      .then((response) => {
        if (!isMountedRef.current) return;
        const data = response.data as SubnetPool[];
        setPools(data);
      })
      .catch((err) => toastError(err, "Getting subnet pools failed!"))
      .finally(() => setLoading(false));
  }, [region, projectId, isMountedRef]);

  const options = useMemo(() => {
    return pools.map((pool) => ({
      key: pool.id,
      value: pool.id,
      text: `${pool.name} (Prefix: /${pool.default_prefixlen})`,
    }));
  }, [pools]);

  useEffect(fetchSubnetPools, [fetchSubnetPools]);

  useEffect(() => {
    if (pools.length === 1 && pools[0].id !== subnetPool) {
      onChange({} as React.FormEvent<HTMLElement>, {
        name: "subnetPool",
        value: pools[0].id,
      });
    }
  }, [pools, subnetPool, onChange]);

  return { pools, loading, options };
};

export default useSubnetPools;
